import type { CartSummary, GlobalContent } from "@wbly/data-storefront";
import { useStore } from "@nanostores/react";
import { cartDetails } from "../../../pages/[locale]/cart/stores/details.store";
import {
  deleteDiscount,
  discount,
} from "../../../pages/[locale]/cart/stores/discount.store";
import { interpolateText } from "../../../util/interpolate-text";
import styles from "./CartSummary.module.css";

const {
  outer,
  title,
  lineItem,
  lineItemAmount,
  discountContainer,
  discountSection,
  discountLabel,
  discountAmount,
  promoCodeSection,
  removePromoButton,
  discountedTotal,
  discountedTotalLabel,
  discountedTotalAmount,
} = styles;

type CartSummaryProps = {
  staticContent: CartSummary & GlobalContent;
};

export function CartSummary({ staticContent }: CartSummaryProps) {
  const $cartDetails = useStore(cartDetails);
  const $discount = useStore(discount);

  const order = $cartDetails.data?.order;

  const isDiscountRemoveDisabled =
    $discount.status === "loading" || $cartDetails.status === "loading";

  const hasDiscount =
    order?.totals_v2.discount && order.totals_v2.discount.amount !== "0.0";

  return (
    <section className={outer} data-testid="cart-summary">
      <p className={title}>{staticContent.title}</p>

      <div className={lineItem}>
        <p>
          {interpolateText(staticContent.totalLabel, {
            quantity: String(order?.items.length ?? "-"),
          })}
        </p>

        <p className={lineItemAmount} data-status={$cartDetails.status}>
          {order?.totals_v2.items.formatted ?? "-"}
        </p>
      </div>

      {order?.totals_v2.shipping?.formatted && (
        <div className={lineItem}>
          <p>
            {staticContent.estimatedShippingLabel ?? "Estimated shipping"}
            {/* ^ TODO: This label is empty in some Prismic locales. Remove when it's stable */}
          </p>

          <p className={lineItemAmount} data-status={$cartDetails.status}>
            {order.totals_v2.shipping.formatted}
          </p>
        </div>
      )}

      {order?.store_credits?.formatted && (
        <div className={lineItem}>
          <p>{staticContent.checkout.storeCredit}</p>

          <p className={lineItemAmount} data-status={$cartDetails.status}>
            {order.store_credits.formatted}
          </p>
        </div>
      )}

      {hasDiscount && (
        <div
          className={discountContainer}
          data-disabled={isDiscountRemoveDisabled}
        >
          <p className={discountSection}>
            <span className={discountLabel} data-testid="promo-code-label">
              {staticContent.discountAmountLabel}
            </span>

            <span className={discountAmount}>
              {order?.totals_v2.discount.formatted}
            </span>
          </p>

          <p className={promoCodeSection}>
            {order.promoCode && (
              <span>
                {$cartDetails.data.order.promoCode.toLocaleUpperCase()}
                {/* ^ `promoCode` is provided lowercase, but presented in caps */}
              </span>
            )}

            <button
              data-testid="promo-code-remove"
              className={removePromoButton}
              onClick={() => deleteDiscount()}
              disabled={isDiscountRemoveDisabled}
            >
              {staticContent.discountFormRemoveCodeCtaText}
            </button>
          </p>
        </div>
      )}

      <div className={discountedTotal}>
        <p className={discountedTotalLabel}>
          {staticContent.totalDiscountedLabel}
        </p>

        <p className={discountedTotalAmount}>
          {order?.totals_v2.order.formatted ?? "-"}
        </p>
      </div>
    </section>
  );
}
